import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import {
  Component,
  OnInit,
  ElementRef,
  Input,
  OnDestroy,
  Optional,
  Self,
  HostBinding,
  ViewChild,
  EventEmitter,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  Validators,
  FormGroup,
  FormControl,
  FormBuilder,
  NgForm,
  FormGroupDirective,
} from '@angular/forms';

@Component({
  selector: 'app-numerical-input',
  templateUrl: './numerical-input.component.html',
  styleUrls: ['./numerical-input.component.scss'],
  providers: [
    { provide: MatFormFieldControl, useExisting: NumericalInputComponent },
  ],
})
export class NumericalInputComponent
  implements
    OnInit,
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<string>
{
  @Input() public defaultBehavior: boolean = true;
  @Input() public allowOnlyDecimal: boolean = false;
  @Input() public inputStyle: { [key: string]: string } = {};
  @Input() public inputName: string = '';
  @Output() public shouldLabelFloatEvent: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  public form: FormGroup = {} as FormGroup;
  constructor(
    public formBuilder: FormBuilder,
    @Optional() @Self() public ngControl: NgControl,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() private _parentForm: NgForm,
    @Optional() private _parentFormGroup: FormGroupDirective,
  ) {
    this.form = formBuilder.group({ customInput: new FormControl(null) });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }
  // From MatFormFieldControl Interface
  //value
  @Input()
  public get value(): string | null {
    const {
      value: { customInput },
    } = this.form;
    return customInput;
  }
  public set value(inputParam: string | number | null) {
    let customInput = inputParam;
    if (
      customInput === 0 ||
      customInput == '' ||
      customInput == null ||
      !this.defaultBehavior
    ) {
      // console.log('customInput', customInput);
      this.form.setValue({ customInput });
    } else if (this.defaultBehavior) {
      this.form.setValue({
        customInput: Number(customInput).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      });
    }
    this.stateChanges.next();
  }
  //stateChanges
  public stateChanges = new Subject<void>();
  //id
  public static nextId = 0;
  @HostBinding('attr.id')
  public id = `numerical-input-${NumericalInputComponent.nextId++}`;
  //placeholder
  @Input()
  public get placeholder(): string {
    return this._placeholder;
  }
  public set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string = '';
  //ngControl
  // ngControl: NgControl = null;
  //focused
  public focused = false;
  public onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.shouldLabelFloatEvent.emit(this.shouldLabelFloat);
      this.focused = true;
      this.stateChanges.next();
    }
  }
  public onFocusOut(event: FocusEvent) {
    if (
      !this._elementRef.nativeElement.contains(event.relatedTarget as Element)
    ) {
      this.shouldLabelFloatEvent.emit(this.shouldLabelFloat);
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }
  //empty
  public get empty() {
    const {
      value: { customInput },
    } = this.form;

    return !customInput;
  }
  //shouldLabelFloat
  @HostBinding('class.floating')
  public get shouldLabelFloat() {
    return this.focused || !this.empty;
  }
  //required
  @Input()
  public get required(): boolean {
    return (
      this._required ||
      coerceBooleanProperty(
        this.ngControl.control?.hasValidator(Validators.required),
      )
    );
  }
  public set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;
  //disabled
  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }
  public set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.form.disable() : this.form.enable();
    this.stateChanges.next();
  }
  private _disabled = false;
  //errorState
  public errorState: boolean = false;
  //controlType
  controlType = 'numerical-input';
  //setDescribedByIds(ids: string[])
  @Input('aria-describedby') public userAriaDescribedBy: string = '';
  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector(
      '.numerical-input__container',
    )!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }
  //onContainerClick(event: MouseEvent)
  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this._elementRef.nativeElement.querySelector('input')?.focus();
    }
  }
  // From ControlValueAccessor Interface
  public touched = false;
  private onTouched = () => {};
  private onChange = (_: any) => {};
  writeValue(input: string | null): void {
    this.value = input;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  // Custom
  @Input()
  public get percentage(): boolean {
    return this._percentage;
  }
  public set percentage(value: boolean) {
    this._percentage = value;
  }
  private _percentage: boolean = false;
  // Custom
  @Input()
  public get currency(): string {
    return this._currency;
  }
  public set currency(value: string) {
    this._currency = value;
  }
  private _currency: string = '';
  //
  @ViewChild('input') private _input: ElementRef<HTMLInputElement> =
    {} as ElementRef<HTMLInputElement>;
  public _handleInput(): void {
    let inputValue = this.value;
    if (inputValue) {
      inputValue = this._handleValue(inputValue);
      // this.onChange(
      //   inputValue.replaceAll('.', '').replace(',', '.').replace('$ ', ''),
      // );
      this.onChange(
        inputValue
          .replaceAll(',', '') /*.replace('.', ',')*/
          .replace('$ ', ''),
      );
    } else {
      inputValue = null;
      this.onChange(inputValue);
    }
    this._input.nativeElement.value = inputValue ?? '';
  }
  private _handleValue(value: string): string {
    let result = value;
    if (value) {
      // const cleanValue = value.replaceAll('.', '').replace('$', '');
      const cleanValue = value.replaceAll(',', '').replace('$', '');
      // let parts = cleanValue.split(',');
      let parts = cleanValue.split('.');
      // parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (parts.length > 1) {
        parts = [parts[0], parts[1]];
        // result = parts.join(',');
        result = parts.join('.');
      } else {
        result = parts[0];
      }
    }
    result = result.trim();
    if (this._currency === 'inside') {
      if (result === '') {
        return '';
      }
      return `$ ${result}`;
    }
    return result;
  }
  // From OnInit Interface
  ngOnInit(): void {}

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }
  // From OnDestroy Interface
  ngOnDestroy() {
    this.stateChanges.complete();
  }

  private updateErrorState() {
    const parent = this._parentFormGroup || this._parentForm;

    const oldState = this.errorState;
    const newState =
      !!this.ngControl.invalid &&
      (this.touched ||
        this.ngControl.touched ||
        this.ngControl.dirty ||
        parent.submitted);
    if (oldState !== newState) {
      this.errorState = newState;
      this.stateChanges.next();
    }
  }
}
